/* eslint-disable @typescript-eslint/no-wrapper-object-types */
import { CallConstants } from '../constants';
import { OngoingCallListener } from '../Listener';
import { type ICallsettings } from '../types';

export class CallSettings {
  public static POSITION_TOP_LEFT = 'top-left';
  public static POSITION_TOP_RIGHT = 'top-right';
  public static POSITION_BOTTOM_LEFT = 'bottom-left';
  public static POSITION_BOTTOM_RIGHT = 'bottom-right';
  public static POSITION = {
    TOP_LEFT: 'top-left',
    TOP_RIGHT: 'top-right',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_RIGHT: 'bottom-right',
  } as const;
  public static ASPECT_RATIO_DEFAULT = 'default';
  public static ASPECT_RATIO_CONTAIN = 'contain';
  public static ASPECT_RATIO_COVER = 'cover';
  public static ASPECT_RATIO = {
    DEFAULT: 'default',
    CONTAIN: 'contain',
    COVER: 'cover',
  } as const;

  private defaultLayout: boolean;
  private isAudioOnly: boolean;
  private mode: ICallsettings['mode'];
  private ShowEndCallButton: boolean;
  private ShowMuteAudioButton: boolean;
  private ShowPauseVideoButton: boolean;
  private ShowScreenShareButton: boolean;
  private ShowSwitchModeButton: boolean;
  private StartAudioMuted: boolean;
  private StartVideoMuted: boolean;
  // TODO below
  private localizedObject: Object;
  private ShowRecordingButton: boolean;
  private idleTimeoutPeriod: number;
  private StartRecordingOnCallStart: boolean;
  private callListener: OngoingCallListener;

  private ShowSwitchToVideoCallButton: boolean;
  private VirtualBackground: VirtualBackground;
  private ShowVirtualBackgroundSetting: boolean;

  public constructor(builder?: CallSettingsBuilder) {
    this.defaultLayout = builder!.defaultLayout;
    this.isAudioOnly = builder!.isAudioOnly;
    this.mode = builder!.mode;
    this.ShowEndCallButton = builder!.ShowEndCallButton;
    this.ShowMuteAudioButton = builder!.ShowMuteAudioButton;
    this.ShowPauseVideoButton = builder!.ShowPauseVideoButton;
    this.ShowScreenShareButton = builder!.ShowScreenShareButton;
    this.ShowSwitchModeButton = builder!.ShowSwitchModeButton;
    this.StartAudioMuted = builder!.StartAudioMuted;
    this.StartVideoMuted = builder!.StartVideoMuted;
    this.localizedObject = builder!.localizedObject;
    this.ShowRecordingButton = builder!.ShowRecordingButton;
    this.idleTimeoutPeriod = builder!.idleTimeoutPeriod;
    this.StartRecordingOnCallStart = builder!.StartRecordingOnCallStart;
    this.callListener = builder!.callListener;
    this.ShowSwitchToVideoCallButton = builder!.ShowSwitchToVideoCallButton;
    this.VirtualBackground = builder!.virtualBackground;
    this.ShowVirtualBackgroundSetting = builder!.ShowVirtualBackgroundSetting;
  }

  public getCallListener(): OngoingCallListener {
    return this.callListener;
  }

  public isRecordingButtonEnabled(): boolean {
    return this.ShowRecordingButton;
  }

  public getIdleTimeoutPeriod(): number {
    return this.idleTimeoutPeriod;
  }

  public shouldStartRecordingOnCallStart(): boolean {
    return this.StartRecordingOnCallStart;
  }

  public isAudioOnlyCall() {
    return this.isAudioOnly;
  }

  public isDefaultLayoutEnabled() {
    return this.defaultLayout;
  }

  public getMode() {
    return this.mode;
  }

  public getStartWithAudioMuted(): boolean {
    return this.StartAudioMuted;
  }

  public getStartWithVideoMuted(): boolean {
    return this.StartVideoMuted;
  }

  public isEndCallButtonEnabled(): boolean {
    return this.ShowEndCallButton;
  }

  public isMuteAudioButtonEnabled(): boolean {
    return this.ShowMuteAudioButton;
  }

  public isPauseVideoButtonEnabled(): boolean {
    return this.ShowPauseVideoButton;
  }

  public isScreenShareButtonEnabled(): boolean {
    return this.ShowScreenShareButton;
  }

  public isModeButtonEnabled(): boolean {
    return this.ShowSwitchModeButton;
  }

  public getLocalizedStringObject(): Object {
    return this.localizedObject;
  }

  public isAudioToVideoButtonEnabled(): boolean {
    return this.ShowSwitchToVideoCallButton;
  }

  public getVirtualBackground(): VirtualBackground {
    return this.VirtualBackground;
  }

  public isVirtualBackgroundSettingEnabled(): boolean {
    return this.ShowVirtualBackgroundSetting;
  }
}

export class CallSettingsBuilder {
  /** @private */ defaultLayout: boolean = true;
  /** @private */ isAudioOnly: boolean = false;
  /** @private */ mode: ICallsettings['mode'] = CallConstants.MODE.DEFAULT;
  /** @private */ ShowEndCallButton: boolean = true;
  /** @private */ ShowMuteAudioButton: boolean = true;
  /** @private */ ShowPauseVideoButton: boolean = true;
  /** @private */ ShowScreenShareButton: boolean = true;
  /** @private */ ShowSwitchModeButton: boolean = true;
  /** @private */ StartAudioMuted: boolean = false;
  /** @private */ StartVideoMuted: boolean = false;
  /** @private */ localizedObject: Object = {};
  /** @private */ ShowRecordingButton: boolean = false;
  /** @private */ idleTimeoutPeriod: number = 180;
  /** @private */ StartRecordingOnCallStart: boolean = false;
  /** @private */ ShowSwitchToVideoCallButton: boolean = true;
  /** @private */ virtualBackground: VirtualBackground =
    new VirtualBackgroundBuilder().build();
  /** @private */ ShowVirtualBackgroundSetting: boolean = true;
  /** @private */ callListener!: OngoingCallListener;

  /**
   *
   * @param {boolean} defaultLayout
   * This methods shows/hides the default button layout.
   * If set to true the default button layout will be shown.
   * If set to false the default button layout will be hidden.
   * Default value is true
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public enableDefaultLayout(defaultLayout: boolean): this {
    this.defaultLayout = defaultLayout;
    return this;
  }

  /**
   *
   * @param {boolean} isAudioOnly
   * This methods sets the type(audio/video) of the call.
   * If set to true, the call will be strictly an audio call.
   * If set to false, the call will be an audio-video call.
   * Default value is false
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public setIsAudioOnlyCall(isAudioOnly: boolean): this {
    this.isAudioOnly = isAudioOnly;
    return this;
  }

  /**
   *
   * @param {string} mode
   * This method sets the mode of the call.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public setMode(mode: ICallsettings['mode']): this {
    this.mode = mode;
    return this;
  }

  /**
   *
   * @param {boolean} showEndCallButton
   * This method shows/hides the end call button.
   * If set to true it will display the end call button.
   * If set to false it will hide the end call button.
   * Default value is true.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public showEndCallButton(showEndCallButton: boolean): this {
    this.ShowEndCallButton = showEndCallButton;
    return this;
  }

  /**
   *
   * @param {boolean} showMuteAudioButton
   * This method shows/hides the mute audio button.
   * If set to true it will display the mute audio button.
   * If set to false it will hide the mute audio button.
   * Default value is true.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public showMuteAudioButton(showMuteAudioButton: boolean): this {
    this.ShowMuteAudioButton = showMuteAudioButton;
    return this;
  }

  /**
   *
   * @param {boolean} showPauseVideoButton
   * This method shows/hides the pause video button.
   * If set to true it will display the pause video button.
   * If set to false it will hide the pause video button.
   * Default value is true.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public showPauseVideoButton(showPauseVideoButton: boolean): this {
    this.ShowPauseVideoButton = showPauseVideoButton;
    return this;
  }

  /**
   *
   * @param {boolean} showScreenShareButton
   * This method shows/hides the screen share button.
   * If set to true it will display the screen share button.
   * If set to false it will hide the screen share button.
   * Default value is true.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public showScreenShareButton(showScreenShareButton: boolean): this {
    this.ShowScreenShareButton = showScreenShareButton;
    return this;
  }

  /**
   *
   * @param {boolean} showModeButton
   * This method shows/hides the switch mode button.
   * If set to true it will display the switch mode button.
   * If set to false it will hide the switch mode button.
   * Default value is true.
   * @returns
   */
  public showModeButton(showModeButton: boolean): this {
    this.ShowSwitchModeButton = showModeButton;
    return this;
  }

  /**
   *
   * @param {Object} localizedStringObject
   * This method will set the localized text in the call screen.
   * @returns
   */
  public setLocalizedStringObject(localizedStringObject: Object): this {
    console.error('setLocalizedStringObject is deprecated and have no effect.');
    this.localizedObject = localizedStringObject;
    return this;
  }

  /**
   *
   * @param {boolean} audioMuted
   * This method allows the call to be started with audio muted.
   * If set to true, the call will start with audio muted.
   * Default value is false.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public startWithAudioMuted(audioMuted: boolean): this {
    this.StartAudioMuted = audioMuted;
    return this;
  }

  /**
   *
   * @param {boolean} videoMuted
   * This method allows the call to be started with video muted.
   * If set to true, the call will start with video muted.
   * Default value is false.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public startWithVideoMuted(videoMuted: boolean): this {
    this.StartVideoMuted = videoMuted;
    return this;
  }

  /**
   *
   * @param {boolean} customCSS
   * This method will set the custom CSS of the call screen.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public setCustomCSS(_: string): this {
    console.error('setCustomCSS is deprecated and have no effect.');
    return this;
  }

  /**
   *
   * @param {boolean} showRecordingButton
   * This method shows/hides the recording button.
   * If set to true it will display the recording button.
   * If set to false it will hide the recording button.
   * Default value is false.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public showRecordingButton(showRecordingButton: boolean): this {
    this.ShowRecordingButton = showRecordingButton;
    return this;
  }

  /**
   *
   * @param {number} idleTimeoutPeriod
   * This method sets the idle timeout period for the call.
   * If set and you are the only one in call, the call will end
   * after the idle timeout period, giving you the option to
   * extend the call 60 seconds before the call ends.
   * Default value is 180 seconds.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public setIdleTimeoutPeriod(idleTimeoutPeriod: number): this {
    this.idleTimeoutPeriod = idleTimeoutPeriod;
    return this;
  }

  /**
   *
   * @param {boolean} startRecordingOnCallStart
   * This method starts the recording as soon as the call start.
   * If set to true it will start the recording as soon as the call start.
   * Default value is false.
   * @returns {CallSettingsBuilder} Returns the CallSettingsBuilder instance
   */
  public startRecordingOnCallStart(startRecordingOnCallStart: boolean): this {
    this.StartRecordingOnCallStart = startRecordingOnCallStart;
    return this;
  }

  /**
   *
   * @param {boolean} _showAudioToVideoSwitchButton
   * This method shows/hides the switch to video call button.
   * If set to true it will display the switch to video call button.
   * If set to false it will hide the switch to video call button.
   * Default value is true.
   * @returns
   */
  public showSwitchToVideoCallButton(
    _showAudioToVideoSwitchButton: boolean,
  ): this {
    console.error(
      'showSwitchToVideoCallButton is deprecated and has no effect.',
    );
    // this.ShowSwitchToVideoCallButton = showAudioToVideoSwitchButton;
    return this;
  }

  /**
   *
   * @param {VirtualBackground} virtualBackground
   * This method will set the virtual background setting.
   * This methods takes an Object of VirtualBackground Class.
   * @deprecated Virtual background feature is deprecated and it has no effect.
   * @returns
   */
  public setVirtualBackground(virtualBackground: VirtualBackground): this {
    console.error('setVirtualBackground is deprecated and has no effect.');
    this.virtualBackground = virtualBackground;
    return this;
  }

  /**
   *
   * @param {boolean} showVirtualBackgroundSetting
   * This method shows/hides the virtual background setting button.
   * If set to true it will display the virtual background setting button.
   * If set to false it will hide the virtual background setting button.
   * Default value is true.
   * @returns
   */
  public showVirtualBackgroundSetting(
    showVirtualBackgroundSetting: boolean,
  ): this {
    this.ShowVirtualBackgroundSetting = showVirtualBackgroundSetting;
    return this;
  }

  /**
   *
   * @param {OngoingCallListener} callListener
   * This method can be used to subscribe to available callings events
   * @returns
   */
  public setCallListener(callListener: OngoingCallListener): this {
    this.callListener = callListener;
    return this;
  }

  /**
   * This method will return an object of the CallSettings class.
   * @returns {CallSettings} Returns the CallSettings instance
   */
  public build(): CallSettings {
    return new CallSettings(this);
  }
}

export class VirtualBackground {
  private AllowBackgroundBlur: boolean;
  private AllowUserImages: boolean;
  private ShowDefaultImages: boolean;
  private SetImages: string[];
  private EnforceBackgroundBlur: number;
  private EnforceBackgroundImage: string;

  public constructor(builder?: VirtualBackgroundBuilder) {
    this.AllowBackgroundBlur = builder!.AllowBackgroundBlur;
    this.AllowUserImages = builder!.AllowUserImages;
    this.ShowDefaultImages = builder!.ShowDefaultImages;
    this.SetImages = builder!.SetImages;
    this.EnforceBackgroundBlur = builder!.EnforceBackgroundBlur;
    this.EnforceBackgroundImage = builder!.EnforceBackgroundImage;
  }

  public shouldAllowBackgroundBlur(): boolean {
    return this.AllowBackgroundBlur;
  }

  public shouldAllowUserImages(): boolean {
    return this.AllowUserImages;
  }

  public shouldShowDefaultImages(): boolean {
    return this.ShowDefaultImages;
  }

  public getImages(): Array<String> {
    return this.SetImages;
  }

  public isBackgroundBlurEnforced(): number {
    return this.EnforceBackgroundBlur;
  }

  public getEnforcedBackgroundImage(): string {
    return this.EnforceBackgroundImage;
  }
}

export class VirtualBackgroundBuilder {
  /** @private */ AllowBackgroundBlur: boolean = true;
  /** @private */ AllowUserImages: boolean = true;
  /** @private */ ShowDefaultImages: boolean = true;
  /** @private */ SetImages: string[] = [];
  /** @private */ EnforceBackgroundBlur: number = 0;
  /** @private */ EnforceBackgroundImage: string = '';

  /**
   *
   * @param {boolean} AllowBackgroundBlur
   * This method will show the background blur option in Virtual Background.
   * If set to true it will show the background blur option.
   * Default value is true.
   * @returns
   */
  public allowBackgroundBlur(AllowBackgroundBlur: boolean): this {
    this.AllowBackgroundBlur = AllowBackgroundBlur;
    return this;
  }

  /**
   *
   * @param {boolean} AllowUserImage
   * This method will allow the user to add custom images as background.
   * If set to true it will allow the user to add custom images as background.
   * Default value is true.
   * @returns
   */
  public allowUserImages(AllowUserImages: boolean): this {
    this.AllowUserImages = AllowUserImages;
    return this;
  }

  /**
   *
   * @param {boolean} ShowDefaultImages
   * This method will show the default images to be used as background.
   * If set to true it will show the default images to be used as background.
   * Default value is true.
   * @returns
   */
  public showDefaultImages(ShowDefaultImages: boolean): this {
    this.ShowDefaultImages = ShowDefaultImages;
    return this;
  }

  /**
   *
   * @param {boolean} SetImages
   * This method will allow user to add custom Images to be used as background. It takes in an array of URLs.
   * @returns
   */
  public setImages(SetImages: string[]): this {
    this.SetImages = SetImages;
    return this;
  }

  /**
   *
   * @param {number} EnforceBackgroundBlur
   * This method will enforce background blur.
   * This method takes number as input which decides the blur level of the background.
   * Default value is 0.
   * @returns
   */
  public enforceBackgroundBlur(EnforceBackgroundBlur: number): this {
    this.EnforceBackgroundBlur = EnforceBackgroundBlur;
    return this;
  }

  /**
   *
   * @param {string} EnforceBackgroundImage
   * This method will enforce background image.
   * If an URL of the image is sent then that image will be set as background.
   * By default no image is set.
   * @returns
   */
  public enforceBackgroundImage(EnforceBackgroundImage: string): this {
    this.EnforceBackgroundImage = EnforceBackgroundImage;
    return this;
  }

  /**
   * This method will return an object of the VirtualBackground class.
   * @returns {VirtualBackground}
   */
  public build(): VirtualBackground {
    return new VirtualBackground(this);
  }
}
